'use client';

import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Define the TourFilter type
export type TourFilter = {
  price_from?: number;
  price_to?: number | undefined;
  nights_count?: number;
  date_start?: string;
  date_end?: string;
  values?: string[];
  categories?: string[];
  tags?: string[];
  orderBy?: string;
  order?: string;
  search?: string;
  adults?: number; // New field
  food?: string; // Updated to array of numbers (IDs)
  children?: number; // New field
  rating?: number; // New field for rating
  locations?: string[]; // New field for locations
  equipment?: string[]; // New field for equipment
  tour?: number; // New field for tour
  tourName?: string; // New field for tour name
};

// Define the store state
type TourStoreState = {
  filter: TourFilter;
  autoUpdateSearchParams: boolean;
  clearTrigger: boolean;
  SORT_VALUES: { label: string; value: string }[];
};

// Define the store actions
type TourStoreActions = {
  clearFilters: () => void;
  setFilter: (filter: Partial<TourFilter>) => void;
  setAutoUpdateSearchParams: (value: boolean) => void;
  applyFilter: (filter: TourFilter) => void;
};

// Create the context store
const [TourStoreProvider, useTourStore] = createContextStore<TourStoreState, TourStoreActions>(
  {
    filter: {
      adults: 2, // Default adults
      children: 0, // Default children
      locations: [], // Default locations
      equipment: [], // Default equipment
    },
    autoUpdateSearchParams: true,
    clearTrigger: false,
    SORT_VALUES: [
      { label: 'Price Descending', value: 'price:desc' },
      { label: 'Price Ascending', value: 'price:asc' },
      { label: 'Newest', value: 'date_start:desc' },
      { label: 'Oldest', value: 'date_start:asc' },
    ],
  },
  set => ({
    clearFilters: () => {
      set(state => {
        state.filter = {
          orderBy: state.filter.orderBy,
          order: state.filter.order,
          adults: 2, // Reset to default
          children: 0, // Reset to default
          locations: [], // Reset locations
          equipment: [], // Reset equipment
        };
        state.clearTrigger = !state.clearTrigger;
      });
    },
    setFilter: filter => {
      set(state => {
        state.filter = {
          ...state.filter,
          ...filter,
        };
      });
    },
    setAutoUpdateSearchParams: value => {
      set(state => {
        state.autoUpdateSearchParams = value;
      });
    },
    applyFilter: (filter: TourFilter) => {
      const pathname = window.location.pathname;
      const newSearchParams = new URLSearchParams();

      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => {
              newSearchParams.append(key, v.toString());
            });
          } else {
            newSearchParams.set(key, value.toString());
          }
        }
      });

      const searchString = newSearchParams.toString();

      window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
    },
  }),
);

// TourFilterParamsSync component
function TourFilterParamsSync() {
  const filter = useTourStore(state => state.filter);
  const autoUpdateSearchParams = useTourStore(state => state.autoUpdateSearchParams);
  const setFilter = useTourStore(state => state.setFilter);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isMounted, setIsMounted] = useState(false);

  // On mount, parse query parameters and update the store
  useEffect(() => {
    const initialFilter: Partial<TourFilter> = {};

    const arrayParams = ['values', 'categories', 'tags', 'locations', 'equipment'];

    // Handle array parameters
    arrayParams.forEach(param => {
      const values = searchParams.getAll(param);
      if (values.length > 0) {
        initialFilter[param as 'values' | 'categories' | 'tags' | 'locations' | 'equipment'] =
          values;
      }
    });

    // Handle other parameters
    searchParams.forEach((value, key) => {
      if (arrayParams.includes(key)) {
        return;
      }
      if (
        key === 'price_from' ||
        key === 'price_to' ||
        key === 'nights_count' ||
        key === 'adults' ||
        key === 'children' ||
        key === 'rating'
      ) {
        const numberValue = Number(value);
        if (!isNaN(numberValue)) {
          initialFilter[
            key as 'price_from' | 'price_to' | 'nights_count' | 'adults' | 'children' | 'rating'
          ] = numberValue;
        }
      } else if (key === 'date_start' || key === 'date_end' || key === 'food') {
        initialFilter[key as 'date_start' | 'date_end' | 'food'] = value;
      } else if (key === 'orderBy' || key === 'order' || key === 'search') {
        initialFilter[key as 'orderBy' | 'order' | 'search'] = value;
      }
    });

    setFilter(initialFilter);
    setIsMounted(true);
  }, [searchParams, setFilter]);

  // Update URL parameters when the filter changes
  useEffect(() => {
    if (!isMounted) return;
    if (!autoUpdateSearchParams) return; // Check the switch

    const newSearchParams = new URLSearchParams();

    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => {
            newSearchParams.append(key, v.toString());
          });
        } else {
          newSearchParams.set(key, value.toString());
        }
      }
    });

    const searchString = newSearchParams.toString();

    window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
  }, [filter, isMounted, pathname, autoUpdateSearchParams]);

  return null;
}

export const buildToursSearchParams = (
  paramsObject: Record<string, any>,
  defaultOrder: string = 'desc',
  defaultOrderBy: string = 'price',
): string => {
  const params = new URLSearchParams();

  // Include default order and orderBy if not provided in paramsObject
  if (!paramsObject.order) {
    params.set('order', defaultOrder);
  }
  if (!paramsObject.orderBy) {
    params.set('orderBy', defaultOrderBy);
  }

  // Loop over the keys in paramsObject
  for (const key in paramsObject) {
    const value = paramsObject[key];
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((v: any) => {
          params.append(key, v.toString());
        });
      } else {
        params.set(key, value.toString());
      }
    }
  }

  return params.toString();
};

// Export the components
export { TourStoreProvider, useTourStore, TourFilterParamsSync };
